import { useEffect } from 'react'

import { isAnalyticsExcludedPath } from '@/components/molecules/shared/mixpanel-guards'
import { isAnalyticsEnabled } from '@/config'
import { usePathname, useRenderedPathname, useSearchParams } from '@/lib/navigation/navigation'

import { analytics } from './analytics.service'
import { dataLayerService } from './datalayer.service'
import { scrubAnalyticsSearch, scrubAnalyticsUrl } from './safe-url'

/**
 * The customer shell's `page_view`, for Mixpanel and GTM, from the first render on.
 *
 * It used to be MixpanelInit's, which now mounts only after the page's first data: a page seen
 * before then was never counted, and the first one got the time Mixpanel loaded. This is mounted
 * with the shell itself; until Mixpanel loads, the analytics service queues each view with its
 * own time and url and flushes them once, in order, on load. Live mode only, as before: staging
 * capture records no `page_view`, and an excluded path none at all.
 *
 * It follows the page on screen (`useRenderedPathname`), not the location, which moves before the
 * next code-split page has rendered and set its title.
 */
export function PageViewTracker() {
  const location = usePathname()
  const rendered = useRenderedPathname()
  const searchParams = useSearchParams()
  const pathname = rendered ?? location
  const search = scrubAnalyticsSearch(searchParams?.toString())

  useEffect(() => {
    if (!pathname || !isAnalyticsEnabled() || isAnalyticsExcludedPath(pathname)) return
    try {
      const page = {
        page_location: pathname,
        page_title: document.title,
        page_referrer: scrubAnalyticsUrl(document.referrer),
      }
      analytics.pageViewed({ ...page, url: search ? `${pathname}?${search}` : pathname })
      dataLayerService.push('page_view', page)
    } catch {
      // Analytics must never break the app
    }
  }, [pathname, search])

  return null
}
